import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import {
  SENTINEL_ENTRY_TYPE,
  SentinelLoadedEntry,
  SentinelMode,
  SentinelRunStatus,
} from 'features/sentinels/types'
import { useMemo } from 'react'

export interface SentinelSummary {
  id: string
  name: string
  /** One-line description of what the sentinel covers. */
  detail: string
  status: SentinelRunStatus
  updatedAt: string
  mode?: SentinelMode
  /** Datapoint entry id of the target tag (target-mode sentinels only). */
  target?: string
  /** Path of the entry's result.json file (set once training is done). */
  resultPath?: string
}

/**
 * List all sentinel entries for a site. Each create is a new dated entry;
 * delete from the UI rather than overwriting.
 */
export const useSentinelList = (siteId: string | null) => {
  const { data, ...rest } = useGetRolodexEntriesByLoadOptions<
    SentinelLoadedEntry[]
  >(
    siteId
      ? {
          type: [SENTINEL_ENTRY_TYPE],
          tags: { site: siteId },
          limit: 200,
        }
      : undefined
  )

  const sentinels = useMemo<SentinelSummary[]>(
    () =>
      (data ?? [])
        .filter((row) => {
          const summary = row.entry.data.summary
          const id = String(row.entry.id || '')
          // Experiment Lab saves also use fim-sentinel; keep them out of prod list.
          if (summary?.mode === 'experiment') return false
          if (id.startsWith('fim-sentinel-exp-')) return false
          // Scratch preview entries (design preview / experiment train handoff)
          // are fim-sentinel too; they are deleted after use but may briefly exist.
          if (summary?.mode === 'preview') return false
          if (id.startsWith('fim-sentinel-preview-')) return false
          return true
        })
        .map((row) => {
          const d = row.entry.data
          const summary = d.summary
          const detail =
            summary?.mode === 'target'
              ? `${summary.target ?? 'target'} · ${summary.drivers ?? '?'} drivers`
              : `${summary?.tags ?? '?'} tags in ${summary?.families ?? '?'} families`
          return {
            id: row.entry.id,
            name: d.name || row.entry.id,
            detail,
            status: summary?.status ?? 'done',
            updatedAt: row.entry.updated_at,
            mode: summary?.mode,
            target: summary?.target,
            resultPath: d.files?.result,
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  )

  return { sentinels, ...rest }
}
